import React from "react";
import ButtonBase from "@mui/material/ButtonBase";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Control, Controller } from "react-hook-form";
import type { Observation } from "@/resources/types";
import { styled } from "@mui/material/styles";
import { Text } from "@imminently/react/ui/core/text";
import { Button } from "@mui/material";


const ColumnWrap = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

//# region SizeBtn

const sizes = [1, 2, 3, 4, 5, 6, 7, 8];

const SizeText = styled(Text)`
  height: 50px;
  line-height: 50px;
`;


const getCssForClass = (mm: number, sizePx: number) => (
  `&.mm${mm} >div >div { width: ${sizePx}px; height: ${sizePx}px; }`
);
const SizeBtnWrap = styled(ButtonBase)`
  border-radius: 0.5rem;
  padding: 0.25rem 1.75rem;
  border: 1px solid transparent;

  ${getCssForClass(1, 4)}
  ${getCssForClass(2, 8)}
  ${getCssForClass(3, 12)}
  ${getCssForClass(4, 16)}
  ${getCssForClass(5, 20)}
  ${getCssForClass(6, 24)}
  ${getCssForClass(7, 28)}
  ${getCssForClass(8, 32)}

  &.active {
    background-color: ${p => p.theme.palette.primary[100]};
    border-color: ${p => p.theme.palette.primary[500]};
  }

  &:hover {
    background-color: ${p => p.theme.palette.primary[50]};
  }
`;
const SizeBtnCircle = styled("div")`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${p => p.theme.palette.primary[500]};

  >div {
    background-color: ${p => p.theme.palette.primary[500]};
    border-radius: 50%;
  }
`;

type SizeBtnProps = {
  value: number;
  isActive: boolean;
  select: (v: number) => unknown;
}
const SizeBtn: React.FC<SizeBtnProps> = p => {
  const { value, isActive, select } = p;
  const finalClassname = React.useMemo(
    () => [`mm${value}`, isActive && "active"].filter(Boolean).join(" "),
    [value, isActive]
  );

  return (
    <SizeBtnWrap className={finalClassname} onClick={() => select(value)}>
      <SizeBtnCircle><div /></SizeBtnCircle>
    </SizeBtnWrap>
  );
};

//# endregion

//# region ReactionBtn

const ReactionBtnC = styled(Button)`
  width: 9rem;
  padding: 0.25rem 0.5rem;
  justify-content: flex-start;
  color: ${p => p.theme.palette.primary[500]};

  &, &:hover {
    border-color: ${p => p.theme.palette.primary[500]};
  }

  &.active {
    background-color: ${p => p.theme.palette.primary[500]};
    color: white;
  }
`;

type ReactionBtnProps = {
  label: string;
  startNode: React.ReactNode;
  isActive: boolean;
  select: () => unknown;
}
const ReactionBtn: React.FC<ReactionBtnProps> = p => {
  const { label, startNode, isActive, select } = p;

  return (
    <ReactionBtnC
      variant='outlined'
      color='secondary'
      startIcon={startNode}
      className={isActive ? "active" : undefined}
      onClick={select}
    >
      <Text>{label}</Text>
    </ReactionBtnC>
  );
};

type ReactionBtnsProps = {
  value: NonNullable<NonNullable<Observation["neuro"]>["pupil"]>["leftReaction"];
  onChange: (v: NonNullable<ReactionBtnsProps["value"]>) => unknown;
}
const ReactionBtns: React.FC<ReactionBtnsProps> = ({ onChange, value }) => (
  <ColumnWrap>
    <ReactionBtn
      startNode={<AddIcon />}
      label="Reacts"
      isActive={value === "+"}
      select={() => onChange("+")}
    />
    <ReactionBtn
      startNode={<RemoveIcon />}
      label='No reaction'
      isActive={value === "-"}
      select={() => onChange("-")}
    />
    <ReactionBtn
      startNode={<Text bold>C</Text>}
      label='Closed'
      isActive={value === "C"}
      select={() => onChange("C")}
    />
  </ColumnWrap>
);

//# endregion

const Table = styled("table")`
  width: 100%;

  th, td {
    &:nth-of-type(1){ width: 28%; }
    &:nth-of-type(2){ width: 12%; }
    &:nth-of-type(n + 3){ width: 30%; }
  }

  td:nth-of-type(1) {
    padding-left: 4rem;
  }

  tr.reaction td {
    padding-top: 1.5rem;
  }
`;

export type PupilsProps = {
  control: Control<Pick<Observation, "neuro">>;
}
export const Pupils: React.FC<PupilsProps> = p => {
  const { control } = p;

  return (
    <Table>
      <thead>
        <tr>
          <th />
          <th />
          <th><Text bold>Left</Text></th>
          <th><Text bold>Right</Text></th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td><Text bold>Size</Text></td>
          <td>
            <ColumnWrap>
              {sizes.map(it => (
                <SizeText key={it} >{it}mm</SizeText>
              ))}
            </ColumnWrap>
          </td>
          <td>
            <Controller
              control={control}
              name="neuro.pupil.leftSize"
              render={({ field: { value, onChange } }) => (
                <ColumnWrap>
                  {sizes.map(it => (
                    <SizeBtn
                      key={it}
                      value={it}
                      isActive={value === it}
                      select={v => onChange(v)}
                    />
                  ))}
                </ColumnWrap>
              )}
            />
          </td>
          <td>
            <Controller
              control={control}
              name="neuro.pupil.rightSize"
              render={({ field: { value, onChange } }) => (
                <ColumnWrap>
                  {sizes.map(it => (
                    <SizeBtn
                      key={it}
                      value={it}
                      isActive={value === it}
                      select={v => onChange(v)}
                    />
                  ))}
                </ColumnWrap>
              )}
            />
          </td>
        </tr>

        <tr className="reaction">
          <td><Text bold>Reaction</Text></td>
          <td />
          <td>
            <Controller
              control={control}
              name="neuro.pupil.leftReaction"
              render={({ field: { value, onChange } }) => <ReactionBtns value={value} onChange={onChange} />}
            />
          </td>
          <td>
            <Controller
              control={control}
              name="neuro.pupil.rightReaction"
              render={({ field: { value, onChange } }) => <ReactionBtns value={value} onChange={onChange} />}
            />
          </td>
        </tr>
      </tbody>
    </Table>
  );
};
